import React, { useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Printer, ChevronLeft } from 'lucide-react';
import { useLab, studentBase, getCourseTemplateForChild, resolveLabChildId } from '../LabContext';
import { NoIndex } from '../LabUI';
import { findDay, computeDayStatus, getWeeksForCourse } from '../data/lessons';

// Print decoration assets — black-and-white friendly. Lives under
// /public/assets/print so the same files work on every print route, for
// every student (max / anita / future leonid / etc.).
const ASSET = '/assets/print';
const NIMNY = (n) => `${ASSET}/nimny/${n}.webp`;
const DECOR = (n) => `${ASSET}/decor/${n}.webp`;

// Pick a small Nimny section icon based on the section title's keywords.
// Keep the mapping in ONE place so it works for ALL lessons, not Week 1 Day 1.
const pickSectionIcon = (rawTitle) => {
  const title = typeof rawTitle === 'string' ? rawTitle.toLowerCase() : '';
  if (!title) return null;
  // Mission start / route / overview
  if (/маршрут|mission route|обзор|overview|план дня|route/.test(title)) {
    return NIMNY('nimny_01_lantern_standing');
  }
  // Math (Layer 1 in Max, "Математика" in both)
  if (/математ|math|число|count|логик|logic/.test(title)) {
    return NIMNY('nimny_09_holding_star');
  }
  // Russian / spelling / writing
  if (/русск|правопис|орфограф|spelling|russian|написа|letters|буквы/.test(title)) {
    return NIMNY('nimny_19_quill_writing');
  }
  // Reading / retelling / text
  if (/чтени|пересказ|read|reading|retell|текст|story|расска/.test(title)) {
    return NIMNY('nimny_03_reading_book');
  }
  // Observation / safety
  if (/наблюден|observ|безопас|safety|внимани|поиск|seek|find/.test(title)) {
    return NIMNY('nimny_16_magnifier_flower');
  }
  // Drawing / creative practice
  if (/рисун|drawing|draw\b|рисуй|творч|creative|поделк|craft/.test(title)) {
    return NIMNY('nimny_09_holding_star');
  }
  // Practice / paper / cards
  if (/практик|practice|карточк|card|бумаг|paper|задани/.test(title)) {
    return NIMNY('nimny_04_writing');
  }
  // Diary / thinking / reflection
  if (/дневник|diary|мышлен|thinking|размышл|reflect|итог|вывод|summary/.test(title)) {
    return NIMNY('nimny_13_thinking');
  }
  // Parent check / mission stamp / reward
  if (/родительск|parent|печать миссии|stamp|награда|reward|нимы за|подпись/.test(title)) {
    return NIMNY('nimny_02_waving');
  }
  // Generic / fallback — a sitting lantern reads "section start" without
  // overloading meaning.
  return NIMNY('nimny_17_lantern_sitting');
};

// Print worksheet — compact AND readable.
// Layout rules:
//  - Tight one-row header, so the FIRST real content section starts on page 1.
//  - Each printWorksheet "page" is a flowing section: its heading sticks with
//    the first child (break-after: avoid), and individual list items / math
//    cards stay atomic (break-inside: avoid). No headings stranded at the
//    bottom of a page; no cards split across pages.
//  - Math sections (title contains "Математ"/"Math") render as numbered
//    worksheet cards with "Что сделать", a "Работа" lined work area, and a
//    long "Ответ" line.
//  - Anita ("junior") course renders list items as activity cards with
//    larger write-lines and a drawing box for "Нарисуй / Draw" prompts.
//  - Bilingual text "Русский / English" — RU primary, EN smaller, lighter,
//    no bullet glyphs.
//  - Parent answer keys are NEVER printed; only `printWorksheet`.

const PrintWorksheet = () => {
  const { weekId, dayId, studentId: routeStudentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tx, user, language, childProgress } = useLab();
  const childId = resolveLabChildId({
    routeStudentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(childId);
  const courseTemplate = getCourseTemplateForChild(user, childId);
  const weeks = getWeeksForCourse(courseTemplate);
  const day = findDay(weekId, dayId, weeks);
  const status = computeDayStatus(childProgress(childId), weekId, dayId, weeks);
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  const isJunior = courseTemplate === 'anita';

  useEffect(() => {
    if (isParent) return;
    if (!day || day.placeholder || status === 'locked') {
      navigate(`${base}/path`, { replace: true });
    }
  }, [day, status, navigate, base, isParent]);

  const structured = day?._content?.printWorksheet;
  const legacyTasks = day?.print?.tasks?.[language] || day?.print?.tasks?.en || [];

  const title = structured?.title
    ? [structured.title, structured.subtitle].filter(Boolean).join(' — ')
    : tx(day?.print?.title);

  if (!day || day.placeholder || (!structured && !legacyTasks.length)) return null;

  return (
    <div
      className={`lab-print lab-print-compact min-h-screen bg-white text-black print:bg-white${isJunior ? ' is-junior' : ''}`}
    >
      <NoIndex />
      <style>{PRINT_CSS}</style>

      <div className="no-print sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
        <Link
          to={`${base}/${weekId}/${dayId}`}
          className="inline-flex items-center gap-1.5 text-neutral-700 hover:text-neutral-900 text-sm font-sans"
          data-testid="print-back"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('back')}
        </Link>
        {isParent && (
          <span
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-400 bg-amber-50 text-amber-800 text-xs font-sans"
            data-testid="parent-preview-badge"
          >
            {language === 'ru' ? 'Режим просмотра родителя' : 'Parent preview mode'}
          </span>
        )}
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-sans hover:bg-neutral-800"
          data-testid="print-trigger"
        >
          <Printer className="w-4 h-4" />
          {t('printAction')}
        </button>
      </div>

      <article className="lab-print-doc">
        <header className="lab-print-head">
          <div className="lab-print-head-row">
            <div className="lab-print-head-titles">
              <div className="lab-print-kicker">Nimny Lab · {t('printSheetTitle')}</div>
              <h1 className="lab-print-title">{title}</h1>
            </div>
            <div className="lab-print-meta">
              <div className="lab-print-meta-cell">
                <span>{t('printNamePrompt')}</span>
                <span className="lab-print-meta-line" />
              </div>
              <div className="lab-print-meta-cell">
                <span>{t('printDatePrompt')}</span>
                <span className="lab-print-meta-line" />
              </div>
            </div>
            <img
              className="lab-print-head-mascot"
              src={NIMNY(isJunior ? 'nimny_01_lantern_standing' : 'nimny_12_lantern_portrait')}
              alt=""
              aria-hidden="true"
              loading="lazy"
            />
          </div>
        </header>

        {structured ? (
          <div className="lab-print-pages">
            {structured.pages?.map((page, pi) => (
              <PrintSection key={pi} page={page} isJunior={isJunior} index={pi} />
            ))}
          </div>
        ) : (
          <ol className="lab-print-legacy-list">
            {legacyTasks.map((line, i) => (
              <li key={i} className="lab-print-legacy-item">{renderInline(line)}</li>
            ))}
          </ol>
        )}

        <footer className="lab-print-foot">
          readnimny.com/lab · Private — for family use only
          <br />
          © 2026 Nimny LLC. All rights reserved.
        </footer>
      </article>
    </div>
  );
};

// ----- Rendering helpers ----------------------------------------------------

// Strip leading bullet glyphs ("•", "·", "—" used as a list marker) so a list
// rendered without bullets does not visually re-introduce them.
const stripLeadingBullet = (text) => {
  if (typeof text !== 'string') return text;
  return text.replace(/^\s*[•·●▪︎▫︎–—]\s+/u, '');
};

// Split "Русский текст / English text" into a styled bilingual block.
// RU primary, EN smaller + lighter. Never splits inside numeric fractions
// like "12.5% / 80".
const renderBilingual = (text) => {
  if (typeof text !== 'string') return text;
  const cleaned = stripLeadingBullet(text);
  let split = -1;
  let pos = cleaned.length;
  while (pos > 0) {
    const idx = cleaned.lastIndexOf(' / ', pos - 1);
    if (idx === -1) break;
    const rest = cleaned.slice(idx + 3).trimStart();
    if (/^[A-Za-z"“]/.test(rest)) { split = idx; break; }
    pos = idx;
  }
  if (split === -1) return renderInline(cleaned);
  const ru = cleaned.slice(0, split);
  const en = cleaned.slice(split + 3);
  return (
    <>
      <span className="lab-print-ru">{renderInline(ru)}</span>
      <span className="lab-print-en">{renderInline(en)}</span>
    </>
  );
};

// Replace runs of 3+ underscores with a styled blank line span.
const renderInline = (text) => {
  if (typeof text !== 'string') return text;
  const parts = text.split(/(_{3,})/);
  return parts.map((p, i) => {
    if (/^_{3,}$/.test(p)) {
      const long = p.length >= 20;
      return <span key={i} className={`lab-print-blank${long ? ' is-long' : ''}`} />;
    }
    return p;
  });
};

// ----- Section heuristics ---------------------------------------------------

const isMathTitle = (title) =>
  typeof title === 'string' && /математ|math/i.test(title);

const isDrawingPrompt = (text) =>
  typeof text === 'string' && /(нарисуй|нарисуйте|draw\b|drawing)/i.test(text);

const isChecklistItem = (s) =>
  typeof s === 'string' && /^\s*(☐|☑|□|■|\[\s*\])/u.test(s);

const startsWithTaskNumber = (s) =>
  typeof s === 'string' && /^\s*\d+[).]/.test(s);

const looksIndented = (s) =>
  typeof s === 'string' && /^\s{2,}\S/.test(s);

const isStepLine = (s) =>
  typeof s === 'string' && /(^|\s)(шаг|шаги|step|steps)\b/i.test(s);

// True if the line clearly already contains its own "Ответ:" prompt — we
// then don't add a duplicate answer line at the bottom of the card.
const containsAnswerPrompt = (s) =>
  typeof s === 'string' && /(^|\s)(ответ|answer)[:：]/i.test(s);

// ----- Junior activity-block detection -------------------------------------
// Parses an Anita list item and returns a structured activity block so the
// renderer can produce REAL worksheet rows (numbered Q rows, word trace
// lines, sorting columns, drawing box, parent checklist) instead of a vague
// empty box. Patterns are kept generic so they fire on any future lesson.

// Drop the bilingual "/ English" suffix so detection works on the RU part.
const stripEnglishSuffix = (text) => {
  if (typeof text !== 'string') return '';
  const idx = text.search(/\s\/\s+[A-Z]/);
  return idx === -1 ? text : text.slice(0, idx);
};

const isParentCheckLine = (s) =>
  typeof s === 'string' &&
  /^(?:\s*parent\s*check\b|\s*родительск(?:ая|ий)\s*провер)/i.test(s.trim());

// Detects "N вопросов / N questions / N задач / N заданий" with 2..10
const detectQuestionCount = (raw) => {
  const m = stripEnglishSuffix(raw)
    .toLowerCase()
    .match(/\b(\d+)\s*(вопрос\w*|задач\w*|задани\w*|question\w*)\b/);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  return n >= 2 && n <= 10 ? n : 0;
};

// "Слова дня: a, b, c" / "Напиши или обведи: a, b, c, d" / "Вставь буквы: …"
const detectWordList = (raw) => {
  const ru = stripEnglishSuffix(raw);
  const m =
    ru.match(/(слов[аы][^:]*|напиши[^:]*|обведи[^:]*|вставь[^:]*|words?[^:]*):\s*([^.]+)\.?/i);
  if (!m) return null;
  const lead = m[1].trim().toLowerCase();
  const wordsRaw = m[2]
    .split(/[,;]/)
    .map((w) => w.trim())
    .filter(Boolean);
  if (wordsRaw.length < 2 || wordsRaw.length > 12) return null;
  // Choose an action label.
  let label = 'Напиши';
  let labelEn = 'Write';
  if (/обведи/.test(lead)) { label = 'Обведи и напиши'; labelEn = 'Trace and write'; }
  else if (/вставь/.test(lead)) { label = 'Вставь букву'; labelEn = 'Fill the letter'; }
  return { words: wordsRaw, label, labelEn };
};

// "Нужно / Хочу / Полезно" sorting prompt.
const detectSortingPrompt = (raw) => {
  const ru = stripEnglishSuffix(raw).toLowerCase();
  if (!/нужно/.test(ru) || !/хоч[уе]/.test(ru) || !/полезн/.test(ru)) return null;
  // Extract a "8 cards" hint if present, default 9 slots (3×3).
  const m = ru.match(/(\d+)\s*карточ/);
  const cards = m ? parseInt(m[1], 10) : 9;
  const rows = Math.min(4, Math.max(2, Math.ceil(cards / 3)));
  return { rows };
};

// Parse one junior list line into an activity block descriptor.
const parseJuniorBlock = (raw) => {
  const line = String(raw);
  if (isParentCheckLine(line)) return { kind: 'parentCheck' };
  if (isChecklistItem(line)) return { kind: 'check', prompt: line };
  if (isDrawingPrompt(line)) return { kind: 'draw', prompt: line };
  const sort = detectSortingPrompt(line);
  if (sort) return { kind: 'sort', prompt: line, rows: sort.rows };
  const words = detectWordList(line);
  if (words) return { kind: 'words', prompt: line, ...words };
  const qn = detectQuestionCount(line);
  if (qn) return { kind: 'questions', prompt: line, n: qn };
  return { kind: 'default', prompt: line };
};

// ----- Section components ---------------------------------------------------

const PrintSection = ({ page, isJunior, index }) => {
  const blocks = Array.isArray(page.blocks) ? page.blocks : [];
  const math = isMathTitle(page.title);
  const iconSrc = pickSectionIcon(page.title);
  return (
    <section className={`lab-print-section${index === 0 ? ' is-first' : ''}${isJunior ? ' is-junior' : ''}`}>
      {page.title && (
        <h2 className="lab-print-section-title">
          {iconSrc && (
            <img
              className="lab-print-section-icon"
              src={iconSrc}
              alt=""
              aria-hidden="true"
              loading="lazy"
            />
          )}
          <span className="lab-print-section-title-text">{renderBilingual(page.title)}</span>
        </h2>
      )}
      <div className="lab-print-section-body">
        {blocks.map((block, bi) => {
          if (block.type === 'text') {
            return (
              <p key={bi} className="lab-print-text">
                {renderBilingual(block.text)}
              </p>
            );
          }
          if (block.type === 'list' && Array.isArray(block.items)) {
            // Junior (Anita) always renders activity cards, even for math.
            if (isJunior) return <JuniorList key={bi} items={block.items} />;
            if (math) return <MathList key={bi} items={block.items} />;
            return <PlainList key={bi} items={block.items} />;
          }
          return null;
        })}
      </div>
    </section>
  );
};

// Detect "long-answer" prompts inside a PlainList item and produce an
// extra block-level writing area BELOW the item text. The inline «___»
// underlines are kept as labels; the block is where the child actually
// writes. Skips oral prompts (retell, ✓ markers). Heuristics:
//   • "N-M предложен*" / "N предложен*"  →  ruled area (N × 8mm)
//   • "план из N пункт*" / "N-point plan" →  N numbered short-write rows
//   • "ответ в N-M предложениях"          →  ruled area (M × 8mm)
const OralMarker = /(перескаж|retell|устно|orally|✓)/i;
const detectItemWritingSpace = (raw) => {
  if (typeof raw !== 'string') return null;
  if (OralMarker.test(raw)) return null;
  const line = stripEnglishSuffix(raw).toLowerCase();
  // Plan of N points.
  const plan = line.match(/план\s+из\s+(\d+)\s*пункт/) || line.match(/(\d+)[-\s]?point\s+plan/);
  if (plan) {
    const n = Math.min(6, Math.max(2, parseInt(plan[1], 10) || 3));
    return (
      <div className="lab-print-plan-rows" aria-hidden="true">
        {Array.from({ length: n }).map((_, k) => (
          <div className="lab-print-plan-row" key={k}>
            <span className="lab-print-plan-num">{k + 1})</span>
            <span className="lab-print-plan-line" />
          </div>
        ))}
      </div>
    );
  }
  // N or N-M sentences.
  const sent = line.match(/(\d+)(?:\s*[-–—]\s*(\d+))?\s*предложен/) ||
               line.match(/(\d+)(?:\s*[-–—]\s*(\d+))?\s*sentence/);
  if (sent) {
    const hi = parseInt(sent[2] || sent[1], 10);
    const lines = Math.min(6, Math.max(2, hi));
    return (
      <div className="lab-print-item-lines" aria-hidden="true" style={{ height: `${lines * 8}mm` }} />
    );
  }
  return null;
};

// Plain (non-math) list — no bullet glyphs. Checklist items keep checkbox.
// Short, parallel lists are flowed into two columns to save vertical space.
const PlainList = ({ items }) => {
  const allCheck = items.length > 0 && items.every(isChecklistItem);
  const allShort = items.every((it) => String(it).length <= 80);
  // Disable two-column mode when any item wants a writing block, so the
  // block gets full-width space to breathe.
  const anyExtra = items.some((it) => !!detectItemWritingSpace(it));
  const twoCol = !allCheck && !anyExtra && items.length >= 4 && items.length <= 10 && allShort;
  return (
    <ul className={`lab-print-list ${allCheck ? 'is-check' : ''} ${twoCol ? 'is-two-col' : ''}`}>
      {items.map((it, i) => {
        const extra = detectItemWritingSpace(it);
        return (
          <li key={i} className={`lab-print-list-item${extra ? ' has-extra' : ''}`}>
            {renderBilingual(it)}
            {extra}
          </li>
        );
      })}
    </ul>
  );
};

// Anita / junior — every list item is parsed into a structured activity
// block so the worksheet shows REAL rows the child can fill in (numbered Q
// rows, word trace lines, sorting columns, drawing box, parent checklist),
// not vague empty boxes.
const JuniorList = ({ items }) => {
  return (
    <div className="lab-print-junior-cards">
      {items.map((raw, i) => {
        const block = parseJuniorBlock(raw);
        if (block.kind === 'parentCheck') {
          return <JuniorParentCheck key={i} />;
        }
        if (block.kind === 'check') {
          return (
            <div key={i} className="lab-print-junior-check">
              {renderBilingual(block.prompt)}
            </div>
          );
        }
        if (block.kind === 'draw') {
          return (
            <div key={i} className="lab-print-junior-card is-draw">
              <div className="lab-print-junior-prompt">{renderBilingual(block.prompt)}</div>
              <div className="lab-print-draw-box" aria-hidden="true">
                <img
                  className="lab-print-draw-star"
                  src={DECOR('decor_09')}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                />
              </div>
            </div>
          );
        }
        if (block.kind === 'questions') {
          return (
            <div key={i} className="lab-print-junior-card">
              <div className="lab-print-junior-prompt">{renderBilingual(block.prompt)}</div>
              <div className="lab-print-jq-rows">
                {Array.from({ length: block.n }).map((_, qi) => (
                  <div key={qi} className="lab-print-jq-row">
                    <span className="lab-print-jq-num">{qi + 1})</span>
                    <span className="lab-print-jq-label">
                      Ответ <em>/ Answer</em>:
                    </span>
                    <span className="lab-print-blank is-long" />
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (block.kind === 'words') {
          return (
            <div key={i} className="lab-print-junior-card">
              <div className="lab-print-junior-prompt">{renderBilingual(block.prompt)}</div>
              <div className="lab-print-jw-rows">
                {block.words.map((w, wi) => (
                  <div key={wi} className="lab-print-jw-row">
                    <span className="lab-print-jw-action">
                      {block.label} <em>/ {block.labelEn}</em>:
                    </span>
                    <span className="lab-print-jw-word">{w}</span>
                    <span className="lab-print-blank is-long" />
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (block.kind === 'sort') {
          const slots = block.rows;
          return (
            <div key={i} className="lab-print-junior-card">
              <div className="lab-print-junior-prompt">{renderBilingual(block.prompt)}</div>
              <div className="lab-print-jsort">
                <div className="lab-print-jsort-col">
                  <div className="lab-print-jsort-head">Нужно <em>/ Need</em></div>
                  {Array.from({ length: slots }).map((_, k) => (
                    <div key={k} className="lab-print-jsort-slot" />
                  ))}
                </div>
                <div className="lab-print-jsort-col">
                  <div className="lab-print-jsort-head">Хочу <em>/ Want</em></div>
                  {Array.from({ length: slots }).map((_, k) => (
                    <div key={k} className="lab-print-jsort-slot" />
                  ))}
                </div>
                <div className="lab-print-jsort-col">
                  <div className="lab-print-jsort-head">Полезно <em>/ Useful</em></div>
                  {Array.from({ length: slots }).map((_, k) => (
                    <div key={k} className="lab-print-jsort-slot" />
                  ))}
                </div>
              </div>
            </div>
          );
        }
        const hasInlineBlanks = /_{3,}/.test(block.prompt);
        return (
          <div key={i} className="lab-print-junior-card">
            <div className="lab-print-junior-prompt">{renderBilingual(block.prompt)}</div>
            {!hasInlineBlanks && <div className="lab-print-write-area" aria-hidden="true" />}
          </div>
        );
      })}
    </div>
  );
};

// Generic, child-facing "Parent Check" block — the same compact checklist
// is rendered for ANY lesson whose print data contains a "Parent Check."
// item, so the renderer never has to peek into `parentGuide`.
const JuniorParentCheck = () => (
  <div className="lab-print-junior-card lab-print-jpc">
    <div className="lab-print-junior-prompt">
      <span className="lab-print-ru">Я всё сделал — проверим вместе.</span>
      <span className="lab-print-en">I did it — let&apos;s check together.</span>
    </div>
    <ul className="lab-print-jpc-list">
      {[
        ['Я сделал математику.', 'I did the math.'],
        ['Я прочитал слова.', 'I read the words.'],
        ['Я нарисовал.', 'I drew.'],
        ['Я рассказал родителю.', 'I told my parent.'],
      ].map(([ru, en], i) => (
        <li key={i} className="lab-print-jpc-item">
          <span className="lab-print-jpc-box" aria-hidden="true" />
          <span className="lab-print-ru">{ru}</span>
          <span className="lab-print-en">{en}</span>
        </li>
      ))}
    </ul>
    <div className="lab-print-jpc-sign">
      <span className="lab-print-jpc-sign-label">
        Подпись родителя <em>/ Parent signature</em>:
      </span>
      <span className="lab-print-blank is-long" />
    </div>
  </div>
);

// Math list — parse numbered tasks (and their indented sub-steps) into
// "Что сделать / Работа / Ответ" worksheet cards.
const MathList = ({ items }) => {
  const cards = [];
  let buffer = null;
  const flush = () => {
    if (buffer) {
      cards.push(buffer);
      buffer = null;
    }
  };
  for (const raw of items) {
    const line = String(raw);
    if (startsWithTaskNumber(line)) {
      flush();
      const m = line.match(/^\s*(\d+)[).]\s*(.*)$/);
      buffer = { number: m ? m[1] : '', text: m ? m[2] : line, steps: [] };
    } else if (looksIndented(line) && buffer) {
      buffer.steps.push(line.trim());
    } else if (buffer && /^[A-D]\)/.test(line.trim())) {
      buffer.steps.push(line.trim());
    } else if (buffer && isStepLine(line) && line.length < 200) {
      buffer.steps.push(line.trim());
    } else if (buffer) {
      buffer.text += ' ' + line.trim();
    } else {
      cards.push({ standalone: line });
    }
  }
  flush();

  return (
    <div className="lab-print-math-cards">
      {cards.map((c, i) => {
        if (c.standalone) {
          return (
            <p key={i} className="lab-print-text">
              {renderBilingual(c.standalone)}
            </p>
          );
        }
        const taskHasAnswer =
          c.steps.some(containsAnswerPrompt) || containsAnswerPrompt(c.text);
        const stepHasBlanks =
          c.steps.some((s) => /_{3,}/.test(s)) || /_{3,}/.test(c.text);
        const showEmptyWorkArea = c.steps.length === 0 && !stepHasBlanks;
        return (
          <div key={i} className="lab-print-math-card">
            <div className="lab-print-math-head">
              <span className="lab-print-math-num">{c.number}</span>
              <div className="lab-print-math-task">
                <span className="lab-print-math-label">Задача <em>/ Task</em></span>
                <span className="lab-print-math-text">{renderBilingual(c.text)}</span>
              </div>
            </div>
            <div className="lab-print-math-work">
              <span className="lab-print-math-sublabel">
                Работа <em>/ Work</em>
              </span>
              {c.steps.length > 0 && (
                <div className="lab-print-math-steps">
                  {c.steps.map((s, si) => (
                    <div key={si} className="lab-print-math-step">
                      {renderBilingual(s)}
                    </div>
                  ))}
                </div>
              )}
              {showEmptyWorkArea && (
                <div className="lab-print-work-lines" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>
            {!taskHasAnswer && (
              <div className="lab-print-math-answer">
                <span className="lab-print-math-answer-label">
                  Ответ <em>/ Answer</em>:
                </span>
                <span className="lab-print-blank is-long" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ----- Scoped print stylesheet ----------------------------------------------

const PRINT_CSS = `
  .lab-print-doc {
    max-width: 760px;
    margin: 0 auto;
    padding: 12px 22px 22px;
    color: #111;
    font-family: 'Nunito', 'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-size: 12.5px;
    line-height: 1.32;
  }
  .lab-print-head {
    border-bottom: 1px solid #999;
    padding-bottom: 5px;
    margin-bottom: 7px;
  }
  .lab-print-head-row {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr) auto;
    gap: 14px;
    align-items: end;
  }
  .lab-print-head-titles { min-width: 0; }
  .lab-print-head-mascot {
    width: 20mm;
    height: auto;
    align-self: end;
    filter: grayscale(1) contrast(1.05);
    opacity: 0.92;
    user-select: none;
    -webkit-user-drag: none;
    /* mascot sits on the right edge, never overlaps content */
    justify-self: end;
    margin-bottom: -2mm;
  }
  .lab-print.is-junior .lab-print-head-mascot { width: 24mm; }
  .lab-print-kicker {
    font-size: 9.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #555;
  }
  .lab-print-title {
    font-size: 16px;
    font-weight: 700;
    margin: 2px 0 0;
    line-height: 1.2;
  }
  .lab-print-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    font-size: 10.5px;
    color: #333;
  }
  .lab-print-meta-cell { display: flex; align-items: flex-end; gap: 5px; }
  .lab-print-meta-line { flex: 1; border-bottom: 1px solid #777; height: 12px; }

  .lab-print-pages { display: flex; flex-direction: column; gap: 7px; }

  /* Sections flow naturally; the heading stays with its first child, and
     individual cards/items inside are atomic. */
  .lab-print-section {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
    padding: 7px 11px 9px;
    background: #fff;
    break-inside: auto;
    page-break-inside: auto;
  }
  .lab-print-section.is-first { break-before: avoid; page-break-before: avoid; }
  .lab-print-section-title {
    font-size: 12.5px;
    font-weight: 700;
    margin: 0 0 5px;
    padding-bottom: 3px;
    border-bottom: 1px solid #e2e2e2;
    color: #1a1a1a;
    break-after: avoid;
    page-break-after: avoid;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .lab-print-section-icon {
    width: 14mm;
    height: 14mm;
    object-fit: contain;
    flex-shrink: 0;
    filter: grayscale(1) contrast(1.05);
    opacity: 0.9;
    user-select: none;
    -webkit-user-drag: none;
  }
  .lab-print-section.is-junior .lab-print-section-icon {
    width: 17mm;
    height: 17mm;
  }
  .lab-print-section-title-text { flex: 1; min-width: 0; }
  .lab-print-section-body { display: flex; flex-direction: column; gap: 3px; }

  /* Russian / English bilingual rendering — RU primary, EN secondary. */
  .lab-print-ru { color: #111; }
  .lab-print-en {
    color: #777;
    font-size: 0.82em;
    font-style: italic;
    margin-left: 0.45em;
  }
  .lab-print-en::before { content: '· '; color: #b3b3b3; font-style: normal; }

  .lab-print-text {
    margin: 2px 0 4px;
    font-size: 12px;
    line-height: 1.3;
  }

  /* Plain list — no glyphs, tight rhythm. */
  .lab-print-list {
    list-style: none !important;
    margin: 1px 0 3px;
    padding: 0;
  }
  .lab-print-list.is-two-col { columns: 2; column-gap: 18px; }
  .lab-print-list-item {
    margin: 1px 0;
    padding: 0;
    font-size: 11.8px;
    line-height: 1.3;
    break-inside: avoid;
    page-break-inside: avoid;
    list-style: none !important;
  }
  .lab-print-list-item::marker { content: ''; }
  .lab-print-list.is-check .lab-print-list-item { padding-left: 0; }

  /* Inline blank line — a thin gray underline span. */
  .lab-print-blank {
    display: inline-block;
    min-width: 4em;
    border-bottom: 1px solid #888;
    margin: 0 2px;
    vertical-align: baseline;
    height: 1em;
  }
  .lab-print-blank.is-long { min-width: 16em; }

  /* ---------- Math worksheet cards ------------------------------------ */
  .lab-print-math-cards {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 3px;
  }
  .lab-print-math-card {
    border: 1px solid #d4d4d4;
    border-radius: 6px;
    padding: 7px 10px 8px;
    background: #fafafa;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .lab-print-math-head {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  .lab-print-math-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: #1a1a1a;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    margin-top: 1px;
  }
  .lab-print-math-task {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .lab-print-math-label,
  .lab-print-math-sublabel {
    font-size: 9px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #777;
    font-weight: 700;
  }
  .lab-print-math-label em,
  .lab-print-math-sublabel em {
    color: #aaa;
    font-style: normal;
    font-weight: 500;
  }
  .lab-print-math-text {
    font-size: 12px;
    line-height: 1.3;
  }
  .lab-print-math-work {
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px dashed #d6d6d6;
  }
  .lab-print-math-steps {
    margin-top: 3px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .lab-print-math-step {
    font-size: 11.5px;
    line-height: 1.3;
    color: #1f1f1f;
  }
  .lab-print-work-lines {
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  .lab-print-work-lines span {
    display: block;
    height: 0;
    border-bottom: 1px solid #888;
  }
  .lab-print-math-answer {
    margin-top: 6px;
    padding-top: 5px;
    border-top: 1px dashed #d6d6d6;
    font-size: 11.5px;
    display: flex;
    align-items: baseline;
    gap: 6px;
  }
  .lab-print-math-answer-label { font-weight: 700; color: #1a1a1a; }
  .lab-print-math-answer-label em {
    color: #888;
    font-style: italic;
    font-weight: 500;
    font-size: 0.9em;
  }

  /* ---------- Junior (Anita) activity cards --------------------------- */
  .lab-print-junior-cards {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 3px;
  }
  .lab-print-junior-card {
    border: 1px solid #d4d4d4;
    border-radius: 6px;
    padding: 7px 10px 8px;
    background: #fcfcfa;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .lab-print-junior-card.is-draw { background: #fffefb; }
  .lab-print-junior-prompt {
    font-size: 12.5px;
    line-height: 1.32;
    margin-bottom: 5px;
  }
  .lab-print-write-area {
    height: 55mm;
    border: 1px dashed #b8b8b8;
    border-radius: 4px;
    background:
      repeating-linear-gradient(
        to bottom,
        transparent 0,
        transparent 7mm,
        #c8c8c8 7mm,
        #c8c8c8 calc(7mm + 1px)
      );
  }

  /* Extra writing space injected under a PlainList item when the prompt
     asks for multi-sentence answers or a numbered plan. Kept flush with
     the list indentation so it visually belongs to the same bullet. */
  .lab-print-item-lines {
    margin-top: 3mm;
    margin-bottom: 2mm;
    border: 1px dashed #b8b8b8;
    border-radius: 4px;
    background:
      repeating-linear-gradient(
        to bottom,
        transparent 0,
        transparent 8mm,
        #c8c8c8 8mm,
        #c8c8c8 calc(8mm + 1px)
      );
  }
  .lab-print-plan-rows {
    margin-top: 3mm;
    margin-bottom: 2mm;
    display: flex;
    flex-direction: column;
    gap: 3mm;
  }
  .lab-print-plan-row {
    display: flex;
    align-items: flex-end;
    gap: 4mm;
    min-height: 9mm;
  }
  .lab-print-plan-num {
    flex-shrink: 0;
    font-weight: 700;
    color: #1a1a1a;
    min-width: 1.6em;
    font-size: 11.8px;
  }
  .lab-print-plan-line {
    flex: 1;
    height: 0;
    border-bottom: 1px solid #b8b8b8;
  }
  .lab-print-list-item.has-extra { break-inside: avoid; }
  .lab-print-draw-box {
    position: relative;
    height: 38mm;
    border: 1.5px dashed #b0b0b0;
    border-radius: 6px;
    background: #ffffff;
  }
  .lab-print-draw-star {
    position: absolute;
    top: 3mm;
    right: 3mm;
    width: 10mm;
    height: auto;
    filter: grayscale(1);
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
    -webkit-user-drag: none;
  }
  .lab-print-junior-check {
    font-size: 12px;
    line-height: 1.32;
    padding: 3px 4px;
    border-bottom: 1px dotted #dcdcdc;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .lab-print-junior-check:last-child { border-bottom: 0; }

  /* Junior — numbered "N questions" answer rows. */
  .lab-print-jq-rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 4px;
  }
  .lab-print-jq-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 11.8px;
    line-height: 1.25;
  }
  .lab-print-jq-num {
    flex-shrink: 0;
    font-weight: 700;
    color: #1a1a1a;
    min-width: 1.6em;
    padding-top: 1mm;
  }
  .lab-print-jq-label {
    flex-shrink: 0;
    font-size: 10.5px;
    color: #555;
    font-weight: 600;
    padding-top: 1mm;
  }
  .lab-print-jq-label em { color: #999; font-style: italic; font-weight: 500; font-size: 0.9em; }
  .lab-print-jq-row .lab-print-blank {
    flex: 1;
    min-width: 0;
    height: 14mm;
    border: 0;
    margin: 0;
    background: repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent 6mm,
      #b8b8b8 6mm,
      #b8b8b8 calc(6mm + 1px)
    );
  }

  /* Junior — word tracing / writing rows. */
  .lab-print-jw-rows {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 4px;
  }
  .lab-print-jw-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 11.8px;
    line-height: 1.3;
  }
  .lab-print-jw-action {
    flex-shrink: 0;
    font-size: 9.5px;
    color: #777;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    min-width: 7em;
  }
  .lab-print-jw-action em { color: #aaa; font-style: italic; font-weight: 500; }
  .lab-print-jw-word {
    flex-shrink: 0;
    font-weight: 700;
    color: #1a1a1a;
    font-family: 'Nunito', system-ui, sans-serif;
    letter-spacing: 0.04em;
    min-width: 5em;
  }
  .lab-print-jw-row .lab-print-blank { flex: 1; min-width: 0; }

  /* Junior — sorting columns. */
  .lab-print-jsort {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 5px;
    margin-top: 5px;
  }
  .lab-print-jsort-col {
    display: flex;
    flex-direction: column;
    gap: 3px;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 4px 5px 5px;
    background: #fff;
  }
  .lab-print-jsort-head {
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    color: #1a1a1a;
    border-bottom: 1px solid #d8d8d8;
    padding-bottom: 2px;
  }
  .lab-print-jsort-head em {
    color: #888;
    font-style: italic;
    font-weight: 500;
    font-size: 0.85em;
    margin-left: 3px;
  }
  .lab-print-jsort-slot {
    height: 8mm;
    border-bottom: 1px solid #c4c4c4;
  }

  /* Junior — Parent Check checklist block. */
  .lab-print-jpc { background: #fcfcf8; }
  .lab-print-jpc-list {
    list-style: none;
    margin: 4px 0 6px;
    padding: 0;
  }
  .lab-print-jpc-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 3px 0;
    font-size: 11.8px;
    line-height: 1.3;
    break-inside: avoid;
  }
  .lab-print-jpc-box {
    display: inline-block;
    width: 4mm;
    height: 4mm;
    border: 1.2px solid #444;
    border-radius: 2px;
    flex-shrink: 0;
    margin-right: 2px;
  }
  .lab-print-jpc-sign {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px dashed #d6d6d6;
    font-size: 11px;
  }
  .lab-print-jpc-sign-label { font-weight: 700; color: #1a1a1a; }
  .lab-print-jpc-sign-label em {
    color: #888;
    font-style: italic;
    font-weight: 500;
    font-size: 0.9em;
  }
  .lab-print-jpc-sign .lab-print-blank { flex: 1; min-width: 0; }

  .lab-print-legacy-list { padding: 0 0 0 1.2em; margin: 0; list-style: decimal outside; }
  .lab-print-legacy-item { margin: 4px 0; font-size: 12px; line-height: 1.32; }

  .lab-print-foot {
    margin-top: 10px;
    padding-top: 5px;
    border-top: 1px solid #999;
    font-size: 9.5px;
    text-align: center;
    color: #666;
  }

  /* ---------- Print media tightening ---------------------------------- */
  @media print {
    @page { size: A4; margin: 9mm 11mm 10mm; }
    html, body { background: #fff !important; }
    .no-print { display: none !important; }
    /* Force decorative images to print as B&W and at the size we set, even
       when the browser applies its own color management. */
    .lab-print img {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      filter: grayscale(1) contrast(1.05);
    }
    .lab-print-doc { padding: 0; max-width: none; font-size: 9.8pt; line-height: 1.22; }
    .lab-print-head { margin-bottom: 4mm; padding-bottom: 2mm; }
    .lab-print-head-mascot { width: 18mm; margin-bottom: -1mm; }
    .lab-print.is-junior .lab-print-head-mascot { width: 22mm; }
    .lab-print-title { font-size: 12.5pt; }
    .lab-print-section { box-shadow: none; padding: 2.2mm 3.2mm 2.6mm; }
    .lab-print-section-title { font-size: 10pt; padding-bottom: 1.2mm; margin-bottom: 1.4mm; gap: 2mm; }
    .lab-print-section-icon { width: 12mm; height: 12mm; }
    .lab-print-section.is-junior .lab-print-section-icon { width: 15mm; height: 15mm; }
    .lab-print-text,
    .lab-print-list-item,
    .lab-print-math-text,
    .lab-print-math-step,
    .lab-print-math-answer,
    .lab-print-junior-prompt,
    .lab-print-junior-check { font-size: 9.4pt; line-height: 1.22; }
    .lab-print-en { color: #555; font-size: 0.78em; }
    .lab-print-math-card { background: #fafafa; padding: 1.8mm 2.4mm 2mm; }
    .lab-print-junior-card { background: #fdfdfb; padding: 1.8mm 2.4mm 2mm; }
    .lab-print-write-area { height: 50mm; }
    .lab-print-draw-box { height: 34mm; }
    .lab-print-draw-star { width: 9mm; top: 2.5mm; right: 2.5mm; opacity: 0.5; }
    .lab-print-jq-row,
    .lab-print-jw-row,
    .lab-print-jpc-item { font-size: 9.4pt; }
    .lab-print-jw-action { font-size: 7.6pt; min-width: 22mm; }
    .lab-print-jq-label { font-size: 7.8pt; }
    .lab-print-jsort-head { font-size: 7.8pt; }
    .lab-print-jsort-slot { height: 6.5mm; }
    .lab-print-jpc-box { width: 3.5mm; height: 3.5mm; }
    .lab-print-jpc-sign { font-size: 8.6pt; }
    .lab-print-foot { margin-top: 4mm; }
    /* Last section must not be clipped — keep its tail intact. */
    .lab-print-pages > .lab-print-section:last-child { break-after: avoid; }
  }
`;

export default PrintWorksheet;
